import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled])",
  "select:not([disabled])", "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/**
 * `aria-modal="true"` 는 선언이지 동작이 아니다.
 * 선언만 해 두면 Tab 이 뒤쪽 화면으로 새어 나가고, 스크린리더는 모달이라고
 * 안내하는데 실제로는 뒤 내용이 계속 읽힌다. 키보드로는 닫을 수도 없다.
 *
 * 하는 일
 *  - 열릴 때 안으로 초점을 옮긴다 (첫 입력칸이 있으면 거기로)
 *  - Tab / Shift+Tab 을 안에 가둔다
 *  - Escape 로 닫는다
 *  - 닫을 때 원래 있던 곳으로 초점을 되돌린다 — 어디였는지 잃지 않는다
 *  - 뒤쪽 화면을 `inert` 로 만들어 마우스·스크린리더에도 잠근다
 */
export function useDialog(onClose: () => void): { ref: (el: HTMLElement | null) => void } {
  const nodeRef = useRef<HTMLElement | null>(null);
  const returnRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const ref = useCallback((el: HTMLElement | null) => { nodeRef.current = el; }, []);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    returnRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusables = () => [...node.querySelectorAll<HTMLElement>(FOCUSABLE)]
      .filter((e) => e.offsetParent !== null || e === document.activeElement);

    /* 첫 입력칸이 있으면 거기로. 없으면 첫 버튼. 그것도 없으면 상자 자체. */
    const list = focusables();
    const firstField = list.find((e) => e instanceof HTMLInputElement || e instanceof HTMLTextAreaElement);
    const target = firstField ?? list[0] ?? node;
    if (target === node && !node.hasAttribute("tabindex")) node.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });

    /* 뒤쪽 화면 잠금. 형제만 끄면 되고, 복구도 형제만 되돌리면 된다. */
    const siblings = [...(node.parentElement?.children ?? [])]
      .filter((c): c is HTMLElement => c instanceof HTMLElement && c !== node && !c.hasAttribute("inert"));
    for (const s of siblings) s.setAttribute("inert", "");

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); closeRef.current(); return; }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) { e.preventDefault(); return; }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !node.contains(active))) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault(); first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      for (const s of siblings) s.removeAttribute("inert");
      returnRef.current?.focus({ preventScroll: true });
    };
  }, []);

  return { ref };
}
